// The Talk screen: a whole spoken turn from the browser — the open
// microphone with the Silero detector (always-on), push-to-talk on a
// held key, speech during an answer queued, the reply played as it
// arrives. The turn machine is the brain (talk/turns); this screen is
// the hands: it wires the machine's commands to the socket and the
// mic's events to the machine, and shows what the turn's frames say.
import { createEffect, createMemo, createSignal, onCleanup, onMount, Show, type JSX } from "solid-js";
import type { Down, Up } from "@/gen/psypen/v1/session_pb";
import type { Session } from "@/session/client";
import type { OrbHandle } from "@/orb/OrbFace";
import type { OrbState } from "@/orb/orb";
import { OrbFace } from "@/orb/OrbFace";
import { createTurnMachine, type TurnCmd, type TurnEv } from "@/talk/turns";
import { toUp } from "@/talk/socket";
import { applyReply, applyTranscript, emptyText, shownLine, type TurnText } from "@/talk/dictation";
import { openMic, type Hearing, type MicHandle } from "@/talk/mic";
import { createPlayer } from "@/audio/player";
import { t } from "@/i18n";

// The turn-ending silence and the detector's threshold, the
// pre-image's defaults.
const SILENCE_MS = 1000;
const THRESHOLD = 0.5;

// The player's sink on the browser's own AudioContext at the reply's
// rate, as the orb's player had it.
function ctxSink(): { sink: import("@/audio/ports").AudioSink; analyser: AnalyserNode; close(): void } {
  const ctx = new AudioContext({ latencyHint: "interactive", sampleRate: 24000 });
  const analyser = ctx.createAnalyser();
  analyser.connect(ctx.destination);
  let sources: AudioBufferSourceNode[] = [];
  const sink: import("@/audio/ports").AudioSink = {
    get currentTime() {
      return ctx.currentTime;
    },
    resume() {
      if (ctx.state !== "running") void ctx.resume();
    },
    play(at, samples, ended) {
      const buf = ctx.createBuffer(1, samples.length, ctx.sampleRate);
      buf.getChannelData(0).set(samples);
      const src = ctx.createBufferSource();
      src.buffer = buf;
      src.connect(analyser);
      src.onended = () => {
        sources = sources.filter((s) => s !== src);
        ended();
      };
      src.start(at);
      sources.push(src);
      return buf.duration;
    },
    stopAll() {
      for (const s of sources) {
        s.onended = null;
        try {
          s.stop();
        } catch {
          // already stopped
        }
      }
      sources = [];
    },
  };
  return {
    analyser,
    sink,
    // A closure, not this: close is handed around (onCleanup) and
    // called with no receiver — this.sink was undefined there and the
    // throw broke the unmount mid-cleanup, leaving the old screen
    // standing over the new tab (Nuutti, live 2026-10-10: the tab
    // would not change).
    close() {
      sink.stopAll();
      analyser.disconnect();
      // Idempotent: the player's own cleanup and the frame listener's
      // both land here on unmount; closing twice throws.
      if (ctx.state !== "closed") void ctx.close();
    },
  };
}

export function Talk(props: { session: Session }): JSX.Element {
  const machine = createTurnMachine();
  const [mic, setMic] = createSignal<MicHandle | null>(null);
  const [opening, setOpening] = createSignal(false);
  const [hearing, setHearing] = createSignal<Hearing | "off">("off");
  const [dict, setDict] = createSignal<TurnText>(emptyText());
  const [note, setNote] = createSignal("");
  const [error, setError] = createSignal("");

  const player = (() => {
    const { sink, analyser, close } = ctxSink();
    onCleanup(close);
    return { player: createPlayer(sink), analyser, close };
  })();

  // The sphere, and whether the answer's sound is playing (thinking
  // until the first piece of the answer, speaking once it comes).
  const [orb, setOrb] = createSignal<OrbHandle | null>(null);
  const [soundOut, setSoundOut] = createSignal(false);
  const orbState = createMemo<OrbState>(() => {
    if (props.session.state().pending) return "needs";
    if (machine.status() === "answering") return soundOut() ? "speaking" : "thinking";
    if (machine.status() === "speaking") return "listening";
    if (hearing() !== "off") return "listening";
    return "idle";
  });

  // Latency, the pre-image's way: from the end of the speech (the
  // silence that confirmed it) to the first piece of the answer, text
  // or audio, whichever came first.
  let speechEndedAt = 0;
  let firstAnswerAt = 0;

  function run(cmds: TurnCmd[]): void {
    for (const c of cmds) {
      // A turn opening clears the lines: not only the mic's own start
      // (a speechStart), but a promotion too — speech held during an
      // answer opens its turn without a new speechStart, and the
      // previous answer must make room for its words.
      if (c.t === "turnStart") {
        setDict(emptyText());
        setError("");
        setNote("");
        setSoundOut(false);
        firstAnswerAt = 0;
      }
      const up: Up = toUp(c);
      if (props.session.send(up)) continue;
      if (c.t === "turnStart") {
        // No socket to carry it: the turn is dead on arrival, and so
        // is everything the machine holds — nothing can be sent while
        // the socket is down. Said in words, not silence.
        run(machine.ev({ t: "offline" }));
        setError(t("talk.nosocket", "No connection — the turn did not go out."));
      }
    }
  }

  function feed(e: TurnEv): void {
    if (e.t === "block") {
      // The orb's listening bars read the microphone's blocks as they
      // come (pcm16 little-endian bytes as int16 samples).
      const h = mic();
      if (h && orb()) orb()?.pushMic(new Int16Array(e.pcm.buffer, e.pcm.byteOffset, e.pcm.byteLength >> 1), h.rate);
    }
    if (e.t === "speechEnd") speechEndedAt = performance.now();
    run(machine.ev(e));
    settle();
  }

  // A machine that went idle while the mic was still taking speech
  // ends that taking too — the turn it fed is gone, and the mic's word
  // must not keep saying it hears speech (an error mid-speech, the
  // stop button, a dropped socket).
  function settle(): void {
    if (machine.status() === "idle" && hearing() === "speech") mic()?.interrupt();
  }

  // The first piece of the answer, text or audio, is what the latency
  // note times.
  function markAnswer(): void {
    if (firstAnswerAt || !speechEndedAt) return;
    firstAnswerAt = performance.now();
    const s = ((firstAnswerAt - speechEndedAt) / 1000).toFixed(2);
    setNote(`${t("talk.first", "First reply")} ${s} s ${t("talk.afterspeech", "after the speech ended")}`);
  }

  let openedForHold = false;

  async function toggleMic(): Promise<void> {
    // One open at a time: a second click or a held key while the
    // prompt stands must not open a second stream beside the first.
    if (opening()) return;
    if (mic()) {
      mic()!.close();
      setMic(null);
      setHearing("off");
      openedForHold = false;
      return;
    }
    setOpening(true);
    setHearing("opening");
    setError("");
    try {
      const handle = await openMic({
        silenceMs: SILENCE_MS,
        threshold: THRESHOLD,
        ev: feed,
        hearing: (h) => setHearing(h),
      });
      setMic(handle);
    } catch (e) {
      setError(`${t("talk.mic.failed", "The microphone did not open")} — ${e instanceof Error ? e.message : String(e)}`);
      setHearing("off");
      setMic(null);
      // A hold that could not open the mic holds nothing.
      openedForHold = false;
    } finally {
      setOpening(false);
    }
  }

  // Push-to-talk: hold the key, speak, let go. With the mic open it
  // only takes the turn from the detector's hands; with it closed it
  // opens the mic for the hold — and closes it again on release, so a
  // hold never leaves the microphone open behind it.
  function hold(on: boolean): void {
    if (on) {
      if (mic()) {
        mic()!.press();
        return;
      }
      if (opening()) return;
      openedForHold = true;
      // The continuation reads the signals when it runs, on purpose —
      // the hold began outside them.
      // eslint-disable-next-line solid/reactivity
      void toggleMic().then(() => {
        mic()?.press();
      });
      return;
    }
    mic()?.release();
    if (openedForHold) {
      openedForHold = false;
      mic()?.close();
      setMic(null);
      setHearing("off");
    }
  }

  // The socket dropping takes the running turn with it: no finished
  // ever comes for it (the server ended it with the socket), and the
  // machine must not wait for one — speech held for a turn that is
  // gone would wait forever.
  createEffect(() => {
    if (props.session.status() === "live") return;
    if (machine.status() === "idle") return;
    player.player.stop();
    run(machine.ev({ t: "offline" }));
    settle();
    setError(t("talk.dropped", "The connection dropped — the turn did not finish."));
  });

  onMount(() => {
    const key = (e: KeyboardEvent) => {
      if (e.code !== "Space" || (e.target as HTMLElement | null)?.matches?.("input, textarea, select")) return;
      e.preventDefault(); // on the way up too, or it clicks the focused button
      if (e.type === "keydown" && !e.repeat) hold(true);
      if (e.type === "keyup") hold(false);
    };
    addEventListener("keydown", key);
    addEventListener("keyup", key);
    onCleanup(() => {
      removeEventListener("keydown", key);
      removeEventListener("keyup", key);
    });

    const off = props.session.onFrame(onFrame);
    onCleanup(() => {
      off();
      player.close();
      mic()?.close();
    });
  });

  function onFrame(d: Down): void {
    switch (d.msg.case) {
      case "transcript": {
        const tr = d.msg.value;
        if (d.turn === machine.turn()) setDict((cur) => applyTranscript(cur, tr));
        return;
      }
      case "reply": {
        if (d.turn !== machine.turn()) return;
        const piece = d.msg.value;
        markAnswer();
        setDict((cur) => applyReply(cur, piece));
        return;
      }
      case "audio": {
        if (d.turn !== machine.turn()) return;
        markAnswer();
        setSoundOut(true);
        player.player.push(d.turn, d.msg.value);
        return;
      }
      case "finished": {
        if (d.turn !== machine.turn()) return;
        player.player.end(d.turn);
        setSoundOut(false);
        stop(false);
        return;
      }
      case "error": {
        if (d.turn !== machine.turn() && d.turn !== "") return;
        player.player.stop();
        setSoundOut(false);
        setError(d.msg.value.message);
        stop(false);
        return;
      }
      default:
        return;
    }
  }

  // stop drops the turn by hand (the button) or settles it (finished):
  // the machine goes idle either way; by hand the cancel goes out.
  function stop(byHand: boolean): void {
    if (byHand) {
      player.player.stop();
      run(machine.ev({ t: "abandon" }));
      settle();
      return;
    }
    run(machine.ev({ t: "finished" }));
    settle();
    firstAnswerAt = 0;
  }

  const micWord = (): string => {
    if (hearing() === "off") return t("talk.mic.open", "Open the microphone");
    if (hearing() === "opening") return t("talk.mic.opening", "Opening the microphone…");
    if (machine.status() === "queued") return t("talk.queued", "Turn queued — it goes out when this answer is done");
    if (machine.status() === "answering") return t("talk.answering", "Answering…");
    if (hearing() === "speech") return t("talk.mic.speech", "Hearing speech…");
    return t("talk.mic.quiet", "Microphone open — speak");
  };

  return (
    <section class="screen talk">
      <h1>{t("tab.talk", "Talk")}</h1>
      <div class="orb-stage">
        <OrbFace
          state={orbState()}
          line={shownLine(dict())}
          label={micWord()}
          task={props.session.state().task?.state === "running" || props.session.state().task?.state === "waiting"}
          onTap={() => void toggleMic()}
          handle={(o) => {
            setOrb(o);
            o.setOutput(player.analyser);
          }}
        />
        <p class="orb-word">{micWord()}</p>
        <Show when={machine.status() !== "idle"}>
          <button type="button" onClick={() => stop(true)}>
            {t("talk.stop", "Stop")}
          </button>
        </Show>
        <Show when={note()}>
          <div class="note">{note()}</div>
        </Show>
        <Show when={error()}>
          <div class="err">{error()}</div>
        </Show>
        <p class="hint">
          {t("talk.hold", "Hold Space to talk")} · {t("talk.tap", "click to leave the microphone open")}
        </p>
      </div>
    </section>
  );
}
